import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";
import { useId } from "react";
import "./Field.css";
import type { CtlSize } from "./Button";
import { Icon } from "./Icon";

/** 라벨 · 입력 · 오류 글을 한 덩이로 묶는다 — 공통 v3 의 `.b-field`. 오류는 **글자로** 말한다 —
 *  테두리 색만 빨개지면 색을 못 보는 사람에게는 아무 일도 안 일어난 화면이다. */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: (id: string, invalid: boolean) => ReactNode;
}) {
  const id = useId();
  return (
    <div className={`b-field${error ? " is-invalid" : ""}`}>
      <label className="b-field-label" htmlFor={id}>
        {label}
      </label>
      {children(id, Boolean(error))}
      {error ? (
        <p className="b-field-msg is-error">
          <Icon name="alert" />
          {error}
        </p>
      ) : hint ? (
        <p className="b-field-msg">{hint}</p>
      ) : null}
    </div>
  );
}

/* 네이티브 <input size>·<select size> 는 **글자 수·보이는 줄 수**라 컨트롤 눈금과
   뜻이 다르다. 같은 이름을 두 뜻으로 쓰면 반드시 헷갈리므로 네이티브 쪽을 덮는다. */
type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & { size?: "md" | "lg"; invalid?: boolean };

/** 입력칸 — 공통 `.b-input`(lg 36). 툴바의 검색칸처럼 단추(md 32) 옆에 얹히는 자리만 md 로 내린다 —
 *  공통 `.b-input.is-md`(`.b-select.is-md` 와 짝). */
export function Input({ size = "lg", invalid, className, ...rest }: InputProps) {
  return (
    <input
      {...rest}
      className={["b-input", size === "md" ? "is-md" : "", className].filter(Boolean).join(" ")}
      aria-invalid={invalid || undefined}
    />
  );
}

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> & { size?: CtlSize; invalid?: boolean };

/** 드롭다운은 네이티브 <select> 다 — 공통 `.b-select-wrap > .b-select`. 화살표는 배경 그림이 아니라
 *  감싼 틀 안의 아이콘이다(색 리터럴이 SVG 안에 박히지 않게). 단추 옆 거르기 줄은 md(`.is-md`). */
export function Select({ size = "lg", invalid, children, className, ...rest }: SelectProps) {
  return (
    <span className={["b-select-wrap", className].filter(Boolean).join(" ")}>
      <select
        {...rest}
        className={`b-select${size === "lg" ? "" : " is-md"}`}
        aria-invalid={invalid || undefined}
      >
        {children}
      </select>
      <Icon name="chevronDown" />
    </span>
  );
}
